import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HL_DOT_HEX } from '@/lib/highlight-utils';
import {
  canonHeatmap,
  familyCounts,
  type CanonHeatBook,
  type JournalSources,
} from '@/lib/journal-aggregate';
import styles from './JournalWalk.module.css';

/**
 * "Your walk" — the Scripture Journal home centerpiece (canon-spatial redesign,
 * visual pass). Two stacked pieces, each shown only when it has something to say:
 *
 *  - a stacked MEANING BAND: the proportion of your highlights across the six
 *    meaning families, colored by HL_DOT_HEX (the journal's one real color
 *    system; rule #14 keeps it off the reader's --reader-* tokens);
 *  - a 66-tile CANON HEAT GRID (OT + NT): every book, warmth scaling with how
 *    much you've marked it, so your worn books glow. Marked tiles link to the
 *    book page; unmarked tiles are faint and inert (so they're not sub-44px tap
 *    targets, rule #16).
 *
 * Gated so a brand-new reader doesn't see a wall of empty tiles: the grid needs
 * marks in 2+ books, the band needs 2+ highlights. The band and legend tap into
 * the meaning lens; each legend row is a 44px target (rule #16).
 */

const WALK_MIN_BOOKS = 2;
const BAND_MIN_HIGHLIGHTS = 2;

// Warmth bucket from a book's mark count -> 0..4 (0 = unmarked, inert tile).
function heatBucket(n: number): 0 | 1 | 2 | 3 | 4 {
  if (n <= 0) return 0;
  if (n <= 2) return 1;
  if (n <= 5) return 2;
  if (n <= 10) return 3;
  return 4;
}

// A short tile label: "1 Samuel" -> "1Sa", "Genesis" -> "Gen", "Psalm" -> "Psa".
function abbrev(name: string): string {
  const m = name.match(/^(\d)\s+(\S+)/);
  if (m) return m[1] + m[2].slice(0, 2);
  return name.slice(0, 3);
}

export default function JournalWalk({ sources }: { sources: JournalSources }) {
  const { t } = useTranslation();

  const heat = useMemo(() => canonHeatmap(sources), [sources]);
  const families = useMemo(
    () => familyCounts(sources.highlights),
    [sources.highlights],
  );

  const markedBooks = useMemo(
    () => heat.filter((b) => b.markCount > 0).length,
    [heat],
  );
  const totalHighlights = useMemo(
    () => families.reduce((sum, f) => sum + f.count, 0),
    [families],
  );

  const showGrid = markedBooks >= WALK_MIN_BOOKS;
  const showBand = families.length > 0 && totalHighlights >= BAND_MIN_HIGHLIGHTS;
  if (!showGrid && !showBand) return null;

  const ot = heat.filter((b) => b.order <= 39);
  const nt = heat.filter((b) => b.order >= 40);

  const renderTile = (b: CanonHeatBook) => {
    const bucket = heatBucket(b.markCount);
    const label = abbrev(b.name);
    if (bucket === 0) {
      return (
        <span
          key={b.slug}
          className={`${styles.tile} ${styles.tile0}`}
          aria-hidden="true"
        >
          {label}
        </span>
      );
    }
    return (
      <Link
        key={b.slug}
        to={`/bible/bookmarks/book/${b.slug}`}
        className={`${styles.tile} ${styles[`tile${bucket}`]}`}
        aria-label={`${b.bookTitle ?? b.name}, ${t('bookmark.bookMarkCount', {
          count: b.markCount,
        })}`}
      >
        {label}
      </Link>
    );
  };

  return (
    <section className={styles.walk}>
      <h2 className={styles.heading}>{t('bookmark.walkHeading')}</h2>

      {showBand && (
        <div className={styles.band}>
          <Link
            to="/bible/bookmarks/meaning"
            className={styles.bandLink}
            aria-label={t('bible.journalLensMeaning')}
          >
            <span className={styles.bandBar} aria-hidden="true">
              {families.map((f) => (
                <span
                  key={f.family}
                  className={styles.bandSeg}
                  style={{ flexGrow: f.count, backgroundColor: HL_DOT_HEX[f.family] }}
                />
              ))}
            </span>
          </Link>
          <ul className={styles.legend}>
            {families.map((f) => (
              <li key={f.family}>
                <Link
                  to={`/bible/bookmarks/meaning/${f.family}`}
                  className={styles.legendItem}
                >
                  <span
                    className={styles.legendDot}
                    style={{ backgroundColor: HL_DOT_HEX[f.family] }}
                    aria-hidden="true"
                  />
                  {t(`bible.hlFamily.${f.family}`)}
                  <span className={styles.legendCount}>{f.count}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {showGrid && (
        <div className={styles.grid}>
          <div className={styles.testamentRow}>
            <span className={styles.testamentLabel}>
              {t('bookmark.walkOldTestament')}
            </span>
            <div className={styles.tiles}>{ot.map(renderTile)}</div>
          </div>
          <div className={styles.testamentRow}>
            <span className={styles.testamentLabel}>
              {t('bookmark.walkNewTestament')}
            </span>
            <div className={styles.tiles}>{nt.map(renderTile)}</div>
          </div>
        </div>
      )}
    </section>
  );
}
